import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { useI18n } from "../i18n";

interface ImageCardProps {
  image: {
    _id: Id<"images">;
    url: string | null;
    prompt: string;
    status: "pending" | "generating" | "completed" | "failed";
    errorMessage?: string;
  };
  onClick?: () => void;
}

export default function ImageCard({ image, onClick }: ImageCardProps) {
  const { t } = useI18n();
  const { _id: id, url, prompt, status, errorMessage } = image;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const removeImage = useMutation(api.images.remove);

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!url) return;

    const response = await fetch(url);
    const blob = await response.blob();
    const filename = `generated-${Date.now()}.png`;

    // On iOS/mobile, use Web Share API if available for better UX (allows "Save Image")
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS && navigator.share && navigator.canShare) {
      const file = new File([blob], filename, { type: "image/png" });
      const shareData = { files: [file] };

      if (navigator.canShare(shareData)) {
        try {
          await navigator.share(shareData);
          return;
        } catch (err) {
          // User cancelled or share failed, fall through to download
          if ((err as Error).name === "AbortError") return;
        }
      }
    }

    // Fallback: standard download
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(downloadUrl);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    removeImage({ imageId: id });
    setShowDeleteConfirm(false);
  };

  const handleCancelDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(false);
  };

  const isClickable = status === "completed" && onClick && !showDeleteConfirm;

  return (
    <div
      className={`group relative bg-linen-100 rounded-soft md:rounded-softer overflow-hidden shadow-card hover:shadow-lg active:scale-[0.98] md:active:scale-100 transition-all ${
        isClickable ? "cursor-pointer" : ""
      }`}
      onClick={isClickable ? onClick : undefined}
    >
      {status === "completed" && url ? (
        <div className="w-full aspect-square bg-linen-100">
          <img
            src={url}
            alt={prompt}
            className="w-full h-full object-contain"
          />
        </div>
      ) : status === "failed" ? (
        <div className="w-full aspect-square bg-coral-400/20 flex flex-col items-center justify-center p-4">
          <svg className="w-12 h-12 text-coral-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-sm text-coral-600 text-center font-medium">{t.card.generationFailed}</p>
          {errorMessage && (
            <p className="text-xs text-charcoal-muted text-center mt-1">{errorMessage}</p>
          )}
        </div>
      ) : (
        <div className="w-full aspect-square bg-linen-100 flex flex-col items-center justify-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-sage-400/30 rounded-full" />
            <div className="absolute top-0 left-0 w-16 h-16 border-4 border-sage-500 rounded-full border-t-transparent animate-spin" />
          </div>
          <p className="text-sm text-charcoal-muted mt-4">
            {status === "pending" ? t.card.waitingToStart : t.card.generating}
          </p>
        </div>
      )}

      {/* Delete confirmation overlay */}
      {showDeleteConfirm && (
        <div
          className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center gap-4 z-20 animate-in fade-in duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-white font-medium text-sm md:text-base">{t.card.deleteConfirm}</p>
          <div className="flex gap-3">
            <button
              onClick={handleCancelDelete}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-full text-sm font-medium transition-colors flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              {t.common.cancel}
            </button>
            <button
              onClick={handleConfirmDelete}
              className="px-4 py-2 bg-coral-500 hover:bg-coral-600 text-white rounded-full text-sm font-medium transition-colors flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              {t.common.delete}
            </button>
          </div>
        </div>
      )}

      {/* Prompt overlay - always visible on mobile */}
      {!showDeleteConfirm && (
        <div className="absolute bottom-0 left-0 right-0 p-2 md:p-4 bg-gradient-to-t from-black/60 to-transparent md:opacity-0 md:group-hover:opacity-100 transition-opacity pointer-events-none">
          <p className="text-white text-xs md:text-sm line-clamp-1 md:line-clamp-2">{prompt}</p>
        </div>
      )}

      {/* Click hint for completed images - desktop only */}
      {isClickable && !showDeleteConfirm && (
        <div className="absolute inset-0 hidden md:flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <div className="bg-black/40 rounded-full p-3">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
            </svg>
          </div>
        </div>
      )}

      {/* Action buttons - hidden on mobile, hover on desktop */}
      {status === "completed" && url && !showDeleteConfirm && (
        <div className="absolute top-3 right-3 hidden md:flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={handleDownload}
            className="w-9 h-9 bg-white/90 rounded-full flex items-center justify-center text-charcoal hover:bg-sage-400 hover:text-white active:scale-95 transition-all shadow-soft"
            title={t.common.download}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>
          <button
            onClick={handleDeleteClick}
            className="w-9 h-9 bg-white/90 rounded-full flex items-center justify-center text-charcoal hover:bg-coral-400 hover:text-white active:scale-95 transition-all shadow-soft"
            title={t.common.delete}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      )}

      {/* Delete button for failed images - hidden on mobile */}
      {status === "failed" && !showDeleteConfirm && (
        <button
          onClick={handleDeleteClick}
          className="absolute top-3 right-3 hidden md:flex w-9 h-9 bg-white/90 rounded-full items-center justify-center text-charcoal hover:bg-coral-400 hover:text-white active:scale-95 transition-all shadow-soft"
          title={t.common.delete}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}
